// As-Built page — the source-of-truth view for every physical asset.
// Phase C-AB v1: header, stats, recent imports, import wizard.
// Phase C-AB v2 (next): MapLibre map + list panel + detail drawer.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import ImportWizardModal from '../components/ImportWizardModal';
import AsBuiltMap from '../components/AsBuiltMap';
import PermitDetailDrawer from '../components/PermitDetailDrawer';

type ViewMode = 'split' | 'map' | 'list';

type Project = {
  id: string;
  project_code: string;
  project_name: string;
  county?: string;
  state?: string;
  prime_contractor?: string;
};

type AsBuiltStats = {
  project_id: string;
  counts: Record<string, { total: number; in_service: number; planned: number }>;
};

type AsBuiltImport = {
  id: string;
  project_id: string;
  uploaded_at: string;
  source_format: string;
  source_plan_id: number | null;
  source_project_label: string | null;             // plan label (historical naming)
  source_external_project_id?: number | null;       // Vetro project_id (parent of plan)
  source_external_project_label?: string | null;    // Vetro project name ("Vienna")
  status: string;
  status_message: string | null;
  parse_summary: any;
  diff_summary: any;
  committed_at: string | null;
  is_canonical?: boolean;
};

const TABLE_LABELS: Record<string, string> = {
  segments:   'Segments (cables)',
  poles:      'Poles',
  vaults:     'Vaults / Handholes',
  closures:   'Splice Closures',
  drops:      'Drops',
  equipment:  'Equipment',
  structures: 'Structures',
  hardware:   'Hardware',
  ducts:      'Ducts',
};

const DEFAULT_PROJECT_ID = '85a46d17-50f3-4bcc-be60-68074efcd5c7';   // Larimer01

export default function AsBuiltPage() {
  const [projectId, setProjectId] = useState<string>(DEFAULT_PROJECT_ID);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [selectedFeature, setSelectedFeature] = useState<any>(null);
  // Drill-down: when a blocking-permit row in the segment drawer is clicked,
  // open the full permit drawer ON TOP of the segment drawer. Stacking >
  // navigation — the segment drawer stays mounted so closing the permit
  // drawer lands back on the segment with the new blocker count (cache
  // invalidation in PermitDetailDrawer flushes the readiness query).
  const [selectedPermitId, setSelectedPermitId] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['bead-projects-asbuilt'],
    queryFn: () => api.get('/bead/projects'),
  });
  const project = projects.find(p => p.id === projectId);
  const vistabeamProjects = projects.filter(p => p.prime_contractor === 'Vistabeam');

  const { data: stats } = useQuery<AsBuiltStats>({
    queryKey: ['asbuilt-stats', projectId],
    queryFn: () => api.get(`/as-built/projects/${projectId}/stats`),
    enabled: !!projectId,
  });

  const { data: importsData, refetch: refetchImports } = useQuery<{ items: AsBuiltImport[] }>({
    queryKey: ['asbuilt-imports'],
    queryFn: () => api.get('/as-built/imports'),
    refetchInterval: 5000,
  });
  const imports = (importsData?.items ?? []).filter(i => i.project_id === projectId);

  // Group imports by source_plan_id so we can see the "lanes" of imported data
  const planLanes = new Map<number, AsBuiltImport[]>();
  for (const imp of imports) {
    if (imp.source_plan_id == null) continue;
    if (!planLanes.has(imp.source_plan_id)) planLanes.set(imp.source_plan_id, []);
    planLanes.get(imp.source_plan_id)!.push(imp);
  }

  // Group the plan-lane Map by parent Vetro project (source_external_project_id).
  // Plans without a parent (older imports before the column existed) go into
  // a synthetic "Unattributed" bucket.
  type ProjectGroup = { projectId: number | null; projectLabel: string; planIds: number[] };
  const projectGroups: ProjectGroup[] = [];
  const seenProjects = new Map<string, ProjectGroup>();
  for (const planId of planLanes.keys()) {
    const planImports = planLanes.get(planId)!;
    const sample = planImports[0];
    const projId = sample.source_external_project_id ?? null;
    const projLabel = sample.source_external_project_label ?? (projId == null ? 'Unattributed (legacy imports)' : `Project ${projId}`);
    const key = String(projId ?? 'unattributed');
    if (!seenProjects.has(key)) {
      const g: ProjectGroup = { projectId: projId, projectLabel: projLabel, planIds: [] };
      seenProjects.set(key, g);
      projectGroups.push(g);
    }
    seenProjects.get(key)!.planIds.push(planId);
  }

  // Total objects across all kinds
  const totalObjects = stats
    ? Object.values(stats.counts).reduce((s, c) => s + (c.total || 0), 0)
    : 0;
  const totalInService = stats
    ? Object.values(stats.counts).reduce((s, c) => s + (c.in_service || 0), 0)
    : 0;

  const lastImport = imports[0];

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">As-Built</h1>
          <div className="page__subtitle">
            Source of truth — every physical asset · {project ? `${project.project_code} · ${project.project_name}` : 'Loading…'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={projectId}
            onChange={e => setProjectId(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border)', fontSize: 13, minWidth: 280 }}
          >
            {vistabeamProjects.map(p => (
              <option key={p.id} value={p.id}>{p.project_code} — {p.project_name} ({p.state ?? '—'})</option>
            ))}
          </select>
          {/* View-mode toggle: split / map / list */}
          <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}>
            {(['split', 'map', 'list'] as ViewMode[]).map(m => (
              <button key={m} onClick={() => setViewMode(m)} style={{
                padding: '6px 12px',
                background: viewMode === m ? '#0f172a' : '#fff',
                color: viewMode === m ? '#fff' : '#0f172a',
                border: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                textTransform: 'capitalize',
              }}>{m}</button>
            ))}
          </div>
          <button
            onClick={() => setWizardOpen(true)}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
              color: '#fff',
              border: 0,
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            + Import from Vetro
          </button>
        </div>
      </div>

      {/* Map view (split or map-only modes) */}
      {viewMode !== 'list' && totalObjects > 0 && (
        <div style={{ marginBottom: 16 }}>
          <AsBuiltMap
            projectId={projectId}
            height={viewMode === 'map' ? 'calc(100vh - 220px)' : 460}
            onFeatureClick={setSelectedFeature}
          />
        </div>
      )}

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <Kpi label="Total objects" value={totalObjects.toLocaleString()} accent="#0f172a" />
        <Kpi label="In service" value={totalInService.toLocaleString()} sub={totalObjects > 0 ? `${Math.round(totalInService / totalObjects * 100)}% of total` : ''} accent="#10b981" />
        <Kpi label="Recent imports" value={String(imports.length)} sub={lastImport ? `Last: ${new Date(lastImport.uploaded_at).toLocaleDateString()}` : 'No imports yet'} accent="#3b82f6" />
        <Kpi label="Source" value={lastImport?.source_format ?? '—'} sub={lastImport?.source_project_label ?? 'No imports yet'} accent="#8b5cf6" />
      </div>

      {/* Counts by table */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card__header"><span>Objects by kind</span></div>
        <div className="card__body">
          {!stats && <div style={{ color: 'var(--text-muted)' }}>Loading…</div>}
          {stats && totalObjects === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
              No as-built data for this project yet.
              <div style={{ marginTop: 8 }}>
                <button onClick={() => setWizardOpen(true)} style={{
                  padding: '6px 14px', background: '#1e3a8a', color: '#fff',
                  border: 0, borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}>+ Import from Vetro to get started</button>
              </div>
            </div>
          )}
          {stats && totalObjects > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              {Object.entries(stats.counts)
                .filter(([, c]) => c && c.total > 0)
                .map(([kind, c]) => (
                  <div key={kind} style={{
                    padding: 12, border: '1px solid var(--border)', borderRadius: 6, background: '#fff',
                  }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>
                      {TABLE_LABELS[kind] ?? kind}
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }}>{c.total.toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      {c.in_service} in service · {c.planned} planned
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Plan lanes — grouped by Vetro parent project (Vienna → its 6 plans) */}
      {planLanes.size > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card__header">
            <span>Plan lanes · {planLanes.size} {planLanes.size === 1 ? 'plan' : 'plans'} across {projectGroups.length} Vetro {projectGroups.length === 1 ? 'project' : 'projects'}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
              Each Vetro plan lives in its own lane. Re-imports of a plan only touch that plan's data.
            </span>
          </div>
          <div className="card__body" style={{ padding: 0 }}>
            {projectGroups.map(group => (
              <div key={String(group.projectId ?? 'unattributed')}>
                {/* Vetro project header */}
                <div style={{
                  padding: '10px 16px', background: '#f1f5f9',
                  borderBottom: '1px solid var(--border)', borderTop: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em',
                  color: '#475569',
                }}>
                  <span>📁 {group.projectLabel}</span>
                  <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#94a3b8' }}>
                    {group.projectId != null ? `vetro project_id=${group.projectId} · ` : ''}{group.planIds.length} {group.planIds.length === 1 ? 'plan' : 'plans'}
                  </span>
                </div>
                {/* Plan lanes under this project */}
                {group.planIds.map(planId => {
                  const planImports = planLanes.get(planId)!;
                  const latest = planImports[0];
                  const canonical = planImports.find(i => i.is_canonical);
                  const planLabel = latest.source_project_label || `Plan ${planId}`;
                  return (
                    <div key={planId} style={{
                      padding: '12px 16px 12px 32px', borderBottom: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 12,
                      background: canonical ? '#f0fdf4' : 'white',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 700 }}>{planLabel}</span>
                          <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#94a3b8' }}>plan_id={planId}</span>
                          {canonical && (
                            <span style={{
                              padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700,
                              background: '#dcfce7', color: '#065f46',
                              textTransform: 'uppercase', letterSpacing: '.04em',
                            }}>★ Plan of Record</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                          {planImports.length} {planImports.length === 1 ? 'import' : 'imports'} ·
                          latest: {new Date(latest.uploaded_at).toLocaleString()} ({latest.status.replace(/_/g, ' ')})
                        </div>
                      </div>
                      <CanonicalToggleBtn imp={latest} />
                      <ClearLaneBtn projectId={projectId} planId={planId} planLabel={planLabel} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent imports detail */}
      <div className="card">
        <div className="card__header"><span>Recent imports (all)</span></div>
        <div className="card__body" style={{ padding: 0 }}>
          {imports.length === 0 && (
            <div style={{ padding: 20, color: 'var(--text-muted)', textAlign: 'center' }}>
              No imports yet for this project.
            </div>
          )}
          {imports.map(imp => (
            <ImportRow key={imp.id} imp={imp} onRefresh={refetchImports} />
          ))}
        </div>
      </div>

      {wizardOpen && (
        <ImportWizardModal
          projectId={projectId}
          projectLabel={project ? `${project.project_code} · ${project.project_name}` : ''}
          onClose={() => { setWizardOpen(false); refetchImports(); }}
        />
      )}

      {/* Feature detail drawer — stays mounted while the permit drawer
          drills in on top so the user comes back to it on close. */}
      {selectedFeature && (
        <FeatureDetailDrawer
          feature={selectedFeature}
          projectId={projectId}
          onClose={() => setSelectedFeature(null)}
          onSelectPermit={setSelectedPermitId}
        />
      )}

      {/* Stacked permit drawer — same component the Permits hub uses, so
          status transitions, document uploads, crossing edits all work
          identically from here. */}
      {selectedPermitId && (
        <PermitDetailDrawer
          permitId={selectedPermitId}
          onClose={() => setSelectedPermitId(null)}
          onUpdated={() => {
            // Force the segment drawer's readiness query to refetch so
            // the blocker list immediately reflects the new permit state.
            qc.invalidateQueries({ queryKey: ['as-built-readiness', projectId] });
            qc.invalidateQueries({ queryKey: ['construction-readiness', projectId] });
          }}
        />
      )}
    </>
  );
}

function FeatureDetailDrawer({ feature, projectId, onClose, onSelectPermit }: { feature: any; projectId: string; onClose: () => void; onSelectPermit: (permitId: string) => void }) {
  const props = feature.props ?? {};
  const isSegment = feature.kind === 'segment';

  // Pull readiness for the project and find this segment's blockers + status.
  const { data: readiness } = useQuery<any>({
    queryKey: ['as-built-readiness', projectId],
    queryFn: () => api.get(`/permits/projects/${projectId}/construction-readiness`),
    enabled: !!projectId && isSegment,
    staleTime: 10_000,
  });
  const segReadiness = useMemo(() => {
    if (!isSegment) return null;
    // feature.id is mb_uid after MapLibre's promoteId; fall back to mb_uid
    // explicitly + segment_id in case promotion hasn't been applied yet.
    return (readiness?.segments ?? []).find((s: any) =>
      s.mb_uid === feature.id ||
      s.mb_uid === feature.mb_uid ||
      s.segment_id === feature.id
    ) ?? null;
  }, [readiness, feature.id, feature.mb_uid, isSegment]);

  const ready = !!segReadiness?.ready;
  const blockers: any[] = segReadiness?.blocking_permits ?? [];
  const segStatus = segReadiness?.status ?? feature.status ?? 'planned';

  function statusBg(s: string) {
    return s === 'needed'    ? '#fef3c7' :
           s === 'applied'   ? '#fed7aa' :
           s === 'in_review' ? '#dbeafe' :
           s === 'approved' || s === 'issued' ? '#dcfce7' :
           s === 'denied'    ? '#fecaca' : '#e2e8f0';
  }
  function statusFg(s: string) {
    return s === 'needed'    ? '#92400e' :
           s === 'applied'   ? '#9a3412' :
           s === 'in_review' ? '#1e40af' :
           s === 'approved' || s === 'issued' ? '#166534' :
           s === 'denied'    ? '#7f1d1d' : '#475569';
  }

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.3)', zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 460, maxWidth: '95%', background: '#fff', height: '100vh', overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)' }}
      >
        <div style={{ padding: '14px 20px', background: '#0f172a', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '.05em' }}>{feature.kind}</div>
            <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'monospace' }}>{feature.mb_uid?.slice(0, 12)}…</div>
            {isSegment && (
              <div style={{ fontSize: 12, marginTop: 4 }}>
                Status: <b>{segStatus}</b>
                {ready
                  ? <span style={{ marginLeft: 8, color: '#86efac' }}>✓ permit-clear</span>
                  : <span style={{ marginLeft: 8, color: '#fcd34d' }}>🚧 {blockers.length} blocking</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, color: '#fff', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 20 }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '6px 0', color: '#64748b', width: 140 }}>Status</td>
                <td style={{ padding: '6px 0', fontWeight: 600 }}>{segStatus}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '6px 0', color: '#64748b' }}>Source plan</td>
                <td style={{ padding: '6px 0', fontFamily: 'monospace', fontSize: 11 }}>{feature.source_plan_id ?? '—'}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '6px 0', color: '#64748b' }}>Vetro id</td>
                <td style={{ padding: '6px 0', fontFamily: 'monospace', fontSize: 10 }}>{props.source_external_id ?? '—'}</td>
              </tr>
              {Object.entries(props)
                .filter(([k]) => !['mb_uid', 'source_external_id', 'source_plan_id', 'status', 'import_id'].includes(k))
                .filter(([, v]) => v !== null && v !== '')
                .map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 0', color: '#64748b' }}>{k.replace(/_/g, ' ')}</td>
                    <td style={{ padding: '6px 0' }}>{String(v)}</td>
                  </tr>
                ))}
            </tbody>
          </table>

          {/* Permits anchored to this segment — the real content now that
              Phase B-PERM has shipped. Each row links to /permits where the
              operator can change the permit status and watch this segment's
              readiness update live (cache invalidation pulls it through). */}
          {isSegment && (
            <div style={{ marginTop: 18 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: '#475569',
                textTransform: 'uppercase', letterSpacing: '.06em',
                paddingBottom: 6, borderBottom: '1px solid #e2e8f0', marginBottom: 8,
              }}>
                Permits affecting this segment ({blockers.length === 0 ? 'all clear' : `${blockers.length} blocking`})
              </div>
              {blockers.length === 0 ? (
                <div style={{
                  padding: 10, fontSize: 12, color: '#166534',
                  background: '#dcfce7', border: '1px solid #86efac', borderRadius: 4,
                }}>
                  ✓ Every covering permit is issued, approved, or waived. This segment is permit-clear and ready to construct (pending procurement and crew assignment).
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
                    Construction can't start here until these permits are issued. Click any row to open it.
                  </div>
                  {blockers.map(bp => (
                    <button
                      key={bp.permit_id}
                      onClick={() => onSelectPermit(bp.permit_id)}
                      title="Open this permit (stacks on top of this drawer)"
                      style={{
                        width: '100%', textAlign: 'left',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 10px', marginBottom: 4, borderRadius: 4,
                        background: '#f8fafc', border: '1px solid #e2e8f0',
                        color: '#0f172a', fontSize: 12, cursor: 'pointer',
                        font: 'inherit',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#f8fafc'}
                    >
                      <span>
                        <b style={{ textTransform: 'capitalize' }}>{(bp.kind ?? '').replace(/_/g, ' ')}</b>
                        {bp.feature_owner && <span style={{ color: '#64748b' }}> · {bp.feature_owner}</span>}
                      </span>
                      <span style={{
                        padding: '2px 8px', fontSize: 9, fontWeight: 700,
                        background: statusBg(bp.status), color: statusFg(bp.status),
                        borderRadius: 999, textTransform: 'uppercase', letterSpacing: '.04em',
                      }}>{bp.status.replace(/_/g, ' ')}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="card" style={{ borderTop: accent ? `3px solid ${accent}` : undefined }}>
      <div className="card__body" style={{ padding: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, marginTop: 6, color: '#0f172a' }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function ClearLaneBtn({ projectId, planId, planLabel }: { projectId: string; planId: number; planLabel: string }) {
  const qc = useQueryClient();
  const removeMut = useMutation({
    mutationFn: () => api.del(`/as-built/projects/${projectId}/lanes/${planId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['asbuilt-stats'] });
      qc.invalidateQueries({ queryKey: ['asbuilt-imports'] });
      qc.invalidateQueries({ queryKey: ['asbuilt-geojson'] });
    },
  });
  return (
    <button
      onClick={() => {
        if (confirm(
          `Remove "${planLabel}" from this project?\n\n` +
          `This will:\n` +
          `  • Soft-delete every feature imported from this plan (segments / poles / vaults / etc.)\n` +
          `  • Remove the plan's import history from the Recent imports list\n` +
          `  • Clear the plan-lane card and its map overlay\n\n` +
          `What's preserved for audit:\n` +
          `  • The full change_event log (with before-snapshots of every deleted feature)\n` +
          `  • Any permits anchored to these features (orphan-flagged for resolution)\n\n` +
          `You can re-import the same plan later — it'll come back as a fresh lane.`
        )) {
          removeMut.mutate();
        }
      }}
      disabled={removeMut.isPending}
      title="Remove this Vetro plan from this Milestone project. Soft-deletes features and removes from imports list. Audit trail preserved."
      style={{
        padding: '5px 12px',
        fontSize: 11, fontWeight: 600,
        background: '#fff',
        color: '#b91c1c',
        border: '1px solid #fca5a5',
        borderRadius: 4, cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {removeMut.isPending ? '…' : '🗑 Remove'}
    </button>
  );
}

function CanonicalToggleBtn({ imp }: { imp: AsBuiltImport }) {
  const qc = useQueryClient();
  const isCanon = !!imp.is_canonical;
  const toggleMut = useMutation({
    mutationFn: () =>
      isCanon
        ? api.del(`/as-built/imports/${imp.id}/canonical`)
        : api.post(`/as-built/imports/${imp.id}/canonical`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['asbuilt-imports'] });
    },
  });
  return (
    <button
      onClick={() => toggleMut.mutate()}
      disabled={toggleMut.isPending}
      style={{
        padding: '5px 12px',
        fontSize: 11, fontWeight: 600,
        background: isCanon ? '#fff' : '#1e293b',
        color: isCanon ? '#0f172a' : '#fff',
        border: isCanon ? '1px solid #cbd5e1' : 0,
        borderRadius: 4, cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
      title={isCanon
        ? 'This is the Plan of Record — the official version that drives map defaults, permit anchoring, and federal reports. Click to clear.'
        : 'Set this version as the Plan of Record. Used as the source-of-truth default across the platform.'}
    >
      {toggleMut.isPending ? '…' : (isCanon ? '★ Plan of Record' : 'Set as Plan of Record')}
    </button>
  );
}

function ImportRow({ imp }: { imp: AsBuiltImport; onRefresh: () => void }) {
  const statusColors: Record<string, { bg: string; fg: string }> = {
    uploaded:           { bg: '#f1f5f9', fg: '#475569' },
    parsing:            { bg: '#fef3c7', fg: '#92400e' },
    diffing:            { bg: '#fef3c7', fg: '#92400e' },
    review_pending:     { bg: '#dbeafe', fg: '#1e40af' },
    committed:          { bg: '#dcfce7', fg: '#065f46' },
    cancelled:          { bg: '#f1f5f9', fg: '#64748b' },
    parse_failed:       { bg: '#fee2e2', fg: '#991b1b' },
    validation_failed:  { bg: '#fee2e2', fg: '#991b1b' },
    commit_failed:      { bg: '#fee2e2', fg: '#991b1b' },
  };
  const sc = statusColors[imp.status] ?? statusColors.uploaded;
  const diffS = imp.diff_summary ?? {};
  const parseS = imp.parse_summary ?? {};
  return (
    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            background: sc.bg, color: sc.fg,
            padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '.04em',
          }}>{imp.status.replace(/_/g, ' ')}</span>
          <span style={{ fontWeight: 600, fontSize: 13 }}>
            {imp.source_project_label ?? `Plan ${imp.source_plan_id}`}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            · {imp.source_format} · {new Date(imp.uploaded_at).toLocaleString()}
          </span>
        </div>
        {imp.status === 'committed' && diffS && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Applied: <b>{diffS.new ?? 0}</b> new · <b>{diffS.modified ?? 0}</b> modified · <b>{diffS.deleted ?? 0}</b> deleted · <b>{diffS.unchanged ?? 0}</b> unchanged
          </div>
        )}
        {imp.status === 'review_pending' && diffS && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Pending review: {diffS.new ?? 0} new, {diffS.modified ?? 0} modified, {diffS.deleted ?? 0} to delete
          </div>
        )}
        {imp.status_message && (
          <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4, fontFamily: 'monospace' }}>{imp.status_message}</div>
        )}
        {parseS?.feature_count !== undefined && (
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
            Parsed {parseS.parsed_count}/{parseS.feature_count} features
          </div>
        )}
      </div>
    </div>
  );
}
